import {
    Button,
    FluentProvider,
    Link,
    Spinner,
    makeStyles,
    mergeClasses,
    tokens,
    typographyStyles,
    Text
} from "@fluentui/react-components";
import {
    Add20Filled,
    Checkmark20Regular,
    ChevronLeft20Regular,
    Clock20Regular,
    Dismiss20Regular
} from "@fluentui/react-icons";
import { observer } from "mobx-react";
import * as React from "react";
import StringsStore from "sh-strings/store";
import DateTimeFormatter, { DateTimeFormatType } from "sh-application/utility/DateTimeFormatter";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import TimeZoneUtils from "sh-application/utility/TimeZoneUtils";
import { IRestorableScheduleTeam } from "sh-models";
import { restartApp, UrlFactory } from "sh-application";
import { ECSConfigKey, ECSConfigService, InstrumentationService, TeamDataService } from "sh-services";
import { getGenericEventPropertiesObject } from "sh-instrumentation";

import teamSetupPickerStore, { gotoPrevSetupStep } from "../store/store";
import TeamSetupPickerUtils from "../TeamSetupPickerUtils";
import { TeamSetupPickerStepProps } from "../TeamSetupPickerStepProps";

const NOT_SEEING_PAST_SCHEDULE_LINK = "https://go.microsoft.com/fwlink/?LinkId=2368966";
const NEW_SCHEDULE_SELECTION = "__new_schedule__";
const DEFAULT_EXPIRY_DAYS = 28;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const useStyles = makeStyles({
    // The card chrome (background, rounded corners, shadow) lives on `.root`. The wrapping
    // FluentProvider would otherwise paint its own opaque, square-cornered background of the
    // same color behind the card, making the rounded corners read as sharp. Keep it transparent.
    provider: {
        backgroundColor: "transparent",
        borderRadius: tokens.borderRadiusXLarge
    },
    root: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalXXL,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        borderRadius: tokens.borderRadiusXLarge,
        boxShadow: tokens.shadow64,
        // Fill the dialog surface (width: 100%, not a fixed 600px min) so the card reflows
        // without horizontal scrolling. On small Teams windows the surface goes fullscreen
        // (see TeamSetupPicker.scss onboardingDialogSurface), so grow to the full viewport
        // height and drop the floating-card chrome; the schedules list (`contents`) scrolls
        // internally. Breakpoint kept in sync with onboardingDialogSurface.
        width: "100%",
        height: "460px", // hard coded value of $panel-height: 460px;
        boxSizing: "border-box",
        "@media (max-width: 600px), (max-height: 500px)": {
            height: "100vh",
            borderRadius: 0,
            boxShadow: "none"
        }
    },
    header: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS,
        width: "100%"
    },
    title: {
        flex: "1 1 auto",
        minWidth: 0,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.subtitle1
    },
    description: {
        margin: 0,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.body1
    },
    contents: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalL,
        width: "100%",
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto"
    },
    sectionLabel: {
        color: tokens.colorNeutralForeground1,
        paddingLeft: tokens.spacingHorizontalXXS,
        ...typographyStyles.caption1
    },
    list: {
        display: "flex",
        flexDirection: "column",
        listStyle: "none",
        margin: 0,
        padding: 0,
        width: "100%"
    },
    centerStatusSubstring: {
        color: tokens.colorNeutralForeground3,
        textAlign: "center"
    },
    item: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalM,
        width: "100%",
        height: "64px",
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalXL,
        boxSizing: "border-box",
        backgroundColor: tokens.colorSubtleBackground,
        border: `${tokens.strokeWidthThin} solid ${tokens.colorTransparentStroke}`,
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorNeutralForeground1,
        cursor: "pointer",
        textAlign: "left",
        font: "inherit",
        "&:hover": {
            backgroundColor: tokens.colorSubtleBackgroundHover
        },
        "&:active": {
            backgroundColor: tokens.colorSubtleBackgroundPressed
        },
        "&:focus-visible": {
            outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: "-2px"
        },
        "&:disabled": {
            cursor: "default",
            color: tokens.colorNeutralForegroundDisabled
        }
    },
    itemSelected: {
        backgroundColor: tokens.colorBrandBackground2,
        "&:hover": {
            backgroundColor: tokens.colorBrandBackground2Hover
        },
        "&:active": {
            backgroundColor: tokens.colorBrandBackground2Pressed
        }
    },
    iconSquare: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "40px",
        height: "40px",
        borderRadius: tokens.borderRadiusMedium
    },
    iconSquareBrand: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2
    },
    iconSquareNeutral: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground2
    },
    textContainer: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minWidth: 0
    },
    itemTitle: {
        color: tokens.colorNeutralForeground1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.body1Strong
    },
    itemSubtitle: {
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.caption1
    },
    selectedCheckmark: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: tokens.colorBrandForeground2
    },
    learnWhy: {
        display: "inline-flex",
        paddingLeft: tokens.spacingHorizontalM,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.caption1
    },
    learnWhyLink: {
        marginLeft: tokens.spacingHorizontalXXS
    },
    errorRow: {
        color: tokens.colorPaletteRedForeground1,
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
        ...typographyStyles.caption1
    },
    centerStatus: {
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        rowGap: tokens.spacingVerticalXS,
        paddingTop: tokens.spacingVerticalXXL,
        paddingBottom: tokens.spacingVerticalXXL,
        minHeight: "200px",
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.body1
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: tokens.spacingHorizontalM,
        width: "100%",
        flexShrink: 0,
        marginTop: "auto"
    }
});

const calculateDaysUntilExpiry = (deletedTime: string): number => {
    const deletedMs = new Date(deletedTime).getTime();
    if (isNaN(deletedMs)) {
        return 0;
    }
    const expiryDays = ECSConfigService.getECSFeatureSetting(ECSConfigKey.RestoreScheduleExpiryDays) ?? DEFAULT_EXPIRY_DAYS;
    const expiryMs = deletedMs + expiryDays * MS_PER_DAY;
    const remainingDays = Math.ceil((expiryMs - Date.now()) / MS_PER_DAY);
    return Math.max(0, remainingDays);
};

const formatDeletionSubtitle = (schedule: IRestorableScheduleTeam): string => {
    const strings = StringsStore().registeredStringModules.get("restoreSchedule")!.strings;
    const deletedDate = new Date(schedule.deletedTime);
    const isValidDate = !isNaN(deletedDate.getTime());
    const parts: string[] = [];
    if (isValidDate) {
        const days = calculateDaysUntilExpiry(schedule.deletedTime);
        parts.push(days === 1
            ? strings.get("daysUntilExpirySingular")!
            : strings.get("daysUntilExpiryPlural")!.format(days.toString()));
        const dateStr = DateTimeFormatter.getDateTimeAsString(deletedDate, DateTimeFormatType.Date_Numeric);
        const timeStr = DateTimeFormatter.getDateTimeAsString(deletedDate, DateTimeFormatType.Time);
        const deletedDescriptor = schedule.deletedByMemberName
            ? strings.get("deletedDateTimeByMember")!.format(dateStr, timeStr, schedule.deletedByMemberName)
            : strings.get("deletedDateTime")!.format(dateStr, timeStr);
        parts.push(deletedDescriptor);
    } else if (schedule.deletedByMemberName) {
        parts.push(strings.get("deletedByMember")!.format(schedule.deletedByMemberName));
    }
    return parts.join(strings.get("deletionSubtitleSeparator")!);
};

const formatDateTitle = (schedule: IRestorableScheduleTeam): string => {
    const deletedDate = new Date(schedule.deletedTime);
    if (isNaN(deletedDate.getTime())) {
        return schedule.name;
    }
    return DateTimeFormatter.getDateTimeAsString(deletedDate, DateTimeFormatType.Date_DateMonthYearLong);
};

const TeamSetupRestorableSchedulesPicker: React.FC<TeamSetupPickerStepProps> = observer((props) => {
    const styles = useStyles();
    const restoreScheduleStrings = StringsStore().registeredStringModules.get("restoreSchedule")!.strings;
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [isWorking, setIsWorking] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const selectedTeam = teamSetupPickerStore.teamSetupInfo?.msTeamInfo;
    const restorableSchedules = [...(teamSetupPickerStore.restorableSchedules ?? [])].sort((a, b) => {
        const aTime = new Date(a.deletedTime).getTime();
        const bTime = new Date(b.deletedTime).getTime();
        // Push schedules with an invalid deletion time to the end.
        if (isNaN(aTime)) {
            return isNaN(bTime) ? 0 : 1;
        }
        if (isNaN(bTime)) {
            return -1;
        }
        return bTime - aTime;
    });
    const teamName = selectedTeam?.teamName ?? "";
    const titleText = teamName
        ? restoreScheduleStrings.get("schedulePickerTitleForTeam")!.format(teamName)
        : restoreScheduleStrings.get("schedulePickerTitle")!;

    const logPickerAction = (action: string, extraProperties: { key: string; value: string | number | boolean }[] = []) => {
        InstrumentationService.logEvent(
            InstrumentationService.events.ConvergedOnboarding,
            [
                getGenericEventPropertiesObject(InstrumentationService.properties.EventType, action),
                ...extraProperties.map(p => getGenericEventPropertiesObject(p.key, p.value))
            ]);
    };

    // Step-shown telemetry: emitted once when the restorable-schedules picker is first rendered.
    React.useEffect(() => {
        logPickerAction(InstrumentationService.values.RestorableSchedulesPickerViewed, [
            { key: InstrumentationService.properties.NumberOfRestorableSchedules, value: restorableSchedules.length }
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelect = (id: string) => {
        if (isWorking) {
            return;
        }
        setSelectedId(id);
        setErrorMessage(null);
    };

    const handleClose = () => {
        logPickerAction(InstrumentationService.values.RestorableSchedulesPickerClosed);
        props.onClose();
    };

    const handleBack = () => {
        logPickerAction(InstrumentationService.values.RestorableSchedulesPickerBackClicked);
        gotoPrevSetupStep();
    };

    const handleLearnWhyClick = () => {
        logPickerAction(InstrumentationService.values.RestorableSchedulesPickerLearnWhyClicked);
    };

    const handleDone = async () => {
        if (!selectedId || isWorking) {
            return;
        }
        const isRestoreOperation = selectedId !== NEW_SCHEDULE_SELECTION;
        logPickerAction(InstrumentationService.values.RestorableSchedulesPickerDoneClicked, [
            { key: InstrumentationService.properties.IsRestoreOperation, value: isRestoreOperation }
        ]);
        setErrorMessage(null);
        setIsWorking(true);

        try {
            if (selectedId === NEW_SCHEDULE_SELECTION) {
                if (!selectedTeam) {
                    setErrorMessage(restoreScheduleStrings.get("createScheduleError")!);
                    setIsWorking(false);
                    return;
                }
                const timezone = TimeZoneUtils.getBrowserOlsonCode();
                const schedulingGroupName = StringsStore().registeredStringModules.get("teamSetupPicker")?.strings.get("membersGroupName");
                const newTeamFields = { schedulingGroupName};
                const createdScheduleTeamId = await TeamSetupPickerUtils.provisionScheduleTeam(selectedTeam, timezone, newTeamFields);
                if (createdScheduleTeamId) {
                    window.location.href = UrlFactory.getSchedulesUrl(createdScheduleTeamId);
                    restartApp();
                } else {
                    setErrorMessage(restoreScheduleStrings.get("createScheduleError")!);
                    setIsWorking(false);
                }
            } else {
                const schedule = restorableSchedules.find(s => s.id === selectedId);
                if (!schedule) {
                    setErrorMessage(restoreScheduleStrings.get("restoreScheduleError")!);
                    setIsWorking(false);
                    return;
                }
                await TeamDataService.restoreSchedule(schedule.id, schedule.eTag);
                window.location.href = UrlFactory.getSchedulesUrl(schedule.id);
                restartApp();
            }
        } catch (error) {
            const message = (error as { message?: string })?.message;
            InstrumentationService.trackException(error, "FailedProvisionOrRestoreSchedule");
            setErrorMessage(message ?? restoreScheduleStrings.get("genericError")!);
            setIsWorking(false);
        }
    };

    const renderHeader = (text: string) => (
        <div className={styles.header}>
            <span className={styles.title}>{text}</span>
            <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss20Regular />}
                aria-label={restoreScheduleStrings.get("closeButtonAriaLabel")}
                onClick={handleClose}
            />
        </div>
    );

    if (isWorking && selectedId === NEW_SCHEDULE_SELECTION) {
        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
                <div role="region" aria-label={restoreScheduleStrings.get("creatingScheduleTitle")} className={styles.root}>
                    <div className={styles.centerStatus}>
                        <Spinner
                            size="large"
                            label={restoreScheduleStrings.get("settingUpSchedule")}
                            labelPosition="below"
                        />
                        <Text className={styles.centerStatusSubstring}>
                            {restoreScheduleStrings.get("unknownUserProvisioningSubstringForTeam")}
                        </Text>
                    </div>
                </div>
            </FluentProvider>
        );
    }

    if (isWorking && selectedId && selectedId !== NEW_SCHEDULE_SELECTION) {
        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
                <div role="region" aria-label={restoreScheduleStrings.get("restoringScheduleTitle")} className={styles.root}>
                    <div className={styles.centerStatus}>
                        <Spinner size="large" label={restoreScheduleStrings.get("restoreTeamStartingAriaMessage")} labelPosition="below" />
                        <Text className={styles.centerStatusSubstring}>
                            {restoreScheduleStrings.get("unknownUserProvisioningSubstringForTeam")}
                        </Text>
                    </div>
                </div>
            </FluentProvider>
        );
    }

    return (
        <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
            <div role="region" aria-label={titleText} className={styles.root}>
                {renderHeader(titleText)}
                <p className={styles.description}>
                    {restoreScheduleStrings.get("restorableSchedulesPickerDescription")}
                </p>

                <div className={styles.contents}>
                    <ul className={styles.list}>
                        <li>
                            <Button
                                autoFocus
                                type="button"
                                className={mergeClasses(
                                    styles.item,
                                    selectedId === NEW_SCHEDULE_SELECTION && styles.itemSelected
                                )}
                                aria-pressed={selectedId === NEW_SCHEDULE_SELECTION ? "true" : "false"}
                                disabled={isWorking || !selectedTeam}
                                onClick={() => handleSelect(NEW_SCHEDULE_SELECTION)}
                            >
                                <div
                                    className={mergeClasses(styles.iconSquare, styles.iconSquareBrand)}
                                    aria-hidden="true"
                                >
                                    <Add20Filled />
                                </div>
                                <div className={styles.textContainer}>
                                    <span className={styles.itemTitle}>{restoreScheduleStrings.get("newScheduleItemTitle")}</span>
                                    <span className={styles.itemSubtitle}>
                                        {teamName
                                            ? restoreScheduleStrings.get("newScheduleItemSubtitleForTeam")!.format(teamName)
                                            : restoreScheduleStrings.get("newScheduleItemSubtitle")}
                                    </span>
                                </div>
                                {selectedId === NEW_SCHEDULE_SELECTION && (
                                    <span className={styles.selectedCheckmark} aria-hidden="true">
                                        <Checkmark20Regular />
                                    </span>
                                )}
                            </Button>
                        </li>
                    </ul>

                    {restorableSchedules.length > 0 && (
                        <>
                            <span className={styles.sectionLabel}>{restoreScheduleStrings.get("previouslyDeletedSectionLabel")}</span>
                            <ul className={styles.list}>
                                {restorableSchedules.map(schedule => {
                                    const isSelected = selectedId === schedule.id;
                                    return (
                                        <li key={schedule.id}>
                                            <Button
                                                className={mergeClasses(
                                                    styles.item,
                                                    isSelected && styles.itemSelected
                                                )}
                                                aria-pressed={isSelected ? "true" : "false"}
                                                disabled={isWorking}
                                                onClick={() => handleSelect(schedule.id)}
                                            >
                                                <div
                                                    className={mergeClasses(styles.iconSquare, styles.iconSquareNeutral)}
                                                    aria-hidden="true"
                                                >
                                                    <Clock20Regular />
                                                </div>
                                                <div className={styles.textContainer}>
                                                    <span className={styles.itemTitle}>
                                                        {formatDateTitle(schedule)}
                                                    </span>
                                                    <span className={styles.itemSubtitle}>
                                                        {formatDeletionSubtitle(schedule)}
                                                    </span>
                                                </div>
                                                {isSelected && (
                                                    <span className={styles.selectedCheckmark} aria-hidden="true">
                                                        <Checkmark20Regular />
                                                    </span>
                                                )}
                                            </Button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}

                    <span className={styles.learnWhy}>
                        <Link className={styles.learnWhyLink} href={NOT_SEEING_PAST_SCHEDULE_LINK} target="_blank" rel="noopener noreferrer" inline onClick={handleLearnWhyClick}>
                            {restoreScheduleStrings.get("notSeeingPastSchedule")}
                        </Link>
                    </span>

                    {errorMessage && (
                        <div role="alert" className={styles.errorRow}>
                            {errorMessage}
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    <Button
                        appearance="transparent"
                        icon={<ChevronLeft20Regular />}
                        onClick={handleBack}
                        disabled={isWorking}
                    >
                        {restoreScheduleStrings.get("backButtonLabel")}
                    </Button>
                    <Button
                        appearance="primary"
                        disabled={!selectedId || isWorking}
                        onClick={handleDone}
                    >
                        {restoreScheduleStrings.get("doneButtonLabel")}
                    </Button>
                </div>
            </div>
        </FluentProvider>
    );
});

export default TeamSetupRestorableSchedulesPicker;
